import { Link, useNavigate } from 'react-router-dom';
import { BylineWrap } from '@/components/feed/BylineWrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Check, Users, ChevronRight } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import { trackEvent } from '@/lib/analytics';
import { formatNumber } from '@/lib/utils';

export function WhoToFollow() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAuthed = useAuthStore((s) => !!s.accessToken);

  const { data, isLoading } = useQuery({
    queryKey: ['users', 'suggested'],
    queryFn: () => api.users.getSuggested(5),
    staleTime: 10 * 60 * 1000, // 10 min
  });

  const followMutation = useMutation({
    mutationFn: (username: string) => api.users.follow(username),
    onSuccess: (_data, username) => {
      queryClient.invalidateQueries({ queryKey: ['users', 'suggested'] });
      trackEvent('follow_user_click', { username, source: 'who_to_follow_sidebar', action: 'follow' });
    },
  });

  const handleFollow = (username: string) => {
    if (!isAuthed) {
      navigate('/login');
      return;
    }
    followMutation.mutate(username);
  };

  if (!isLoading && (!data?.items || data.items.length === 0)) {
    return null;
  }

  return (
    <div className="rounded-2xl border border-border-primary bg-bg-secondary overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border-primary bg-gradient-to-r from-sky-500/5 to-transparent">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-500/10">
          <UserPlus className="h-4 w-4 text-sky-500" />
        </div>
        <h3 className="font-semibold text-text-primary">Who to Follow</h3>
      </div>

      <div className="p-2">
        {isLoading ? (
          <div className="space-y-3 p-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="h-9 w-9 rounded-full bg-bg-tertiary flex-shrink-0" />
                <div className="flex-1">
                  <div className="h-3.5 w-20 rounded bg-bg-tertiary" />
                  <div className="mt-1 h-3 w-16 rounded bg-bg-tertiary" />
                </div>
                <div className="h-7 w-16 rounded-lg bg-bg-tertiary flex-shrink-0" />
              </div>
            ))}
          </div>
        ) : (
          data?.items.map((user) => {
            const justFollowed = followMutation.isSuccess && followMutation.variables === user.username;
            const pending = followMutation.isPending && followMutation.variables === user.username;

            return (
              <div
                key={user.id}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-bg-tertiary group"
              >
                {/* T-146: this widget promoted three of our four writers to
                    anonymous visitors via /garage/profile/<name>, which
                    CLIENT-SIDE REDIRECTS strangers to /welcome — decision 133
                    with a redirect instead of a hash. The ceo's decision:
                    /user/<username> is the canonical PUBLIC writer profile.
                    It resolves anonymously and is what our bylines now emit.

                    GATE ON THE RESOLVED USERNAME, NEVER THE DISPLAY NAME
                    (decision 134). The username is the real users.username
                    column, selected server-side on BOTH suggestion paths (the
                    co-member branch and the reputation branch). If it does not
                    resolve we render the row UNLINKED — we do NOT fall back to
                    /garage/profile, because that is the broken destination
                    this task exists to remove.

                    /garage/profile/<name> is NOT 404ed (decision 155): the
                    affordance is withdrawn, the address keeps answering for
                    the authed shell. */}
                <BylineWrap
                  href={typeof user.username === 'string' && user.username.trim() ? `/user/${user.username.trim()}` : null}
                  className="flex min-w-0 flex-1 items-center gap-3"
                >
                  {user.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt=""
                      className="h-9 w-9 rounded-full flex-shrink-0"
                    />
                  ) : (
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-bg-tertiary text-sm font-bold text-text-secondary flex-shrink-0">
                      {(user.displayName || user.username).charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1 truncate">
                      <span className="truncate font-medium text-text-primary group-hover:text-primary-400 transition-colors">
                        {user.displayName || user.username}
                      </span>
                      {user.isVerified && (
                        <span className="flex-shrink-0 rounded bg-primary-500/10 px-1 py-0.5 text-[9px] font-bold text-primary-500">
                          V
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-text-tertiary">
                      <span className="flex items-center gap-0.5">
                        <Users className="h-3 w-3" />
                        {formatNumber(user.followerCount)}
                      </span>
                      {user.reputation > 0 && (
                        <span className="text-reputation">{formatNumber(user.reputation)} rep</span>
                      )}
                    </div>
                  </div>
                </BylineWrap>
                {justFollowed ? (
                  <span className="flex-shrink-0 inline-flex items-center gap-1 rounded-lg bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-400 ring-1 ring-emerald-500/25">
                    <Check className="h-3 w-3" />
                    Followed
                  </span>
                ) : (
                  <button
                    onClick={() => handleFollow(user.username)}
                    disabled={pending}
                    className="flex-shrink-0 rounded-lg bg-primary-500 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
                  >
                    {pending ? '...' : 'Follow'}
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      <Link
        to="/explore?tab=people"
        className="flex items-center justify-center gap-1 border-t border-border-primary py-3 text-sm font-medium text-primary-500 hover:text-primary-400 transition-colors"
      >
        Discover people
        <ChevronRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
